import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authApi, publicApi } from '../services/api';
import { usePasswordReveal } from '../hooks/usePasswordReveal';
import { Loader2, Lock, ArrowRight, CheckCircle2, AlertTriangle, Eye, EyeOff } from 'lucide-react';

interface AppearanceSettings {
  companyName: string;
  companyTagline?: string;
  logo?: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
}

const defaultAppearance: AppearanceSettings = {
  companyName: 'TrackFleet',
  companyTagline: 'GPS Fleet Tracking',
  primaryColor: '#1f8da1',
  secondaryColor: '#6366f1',
  accentColor: '#f59e0b',
};

const APPEARANCE_CACHE_KEY = 'trackfleet:appearance';

function loadCachedAppearance(): AppearanceSettings {
  try {
    const raw = localStorage.getItem(APPEARANCE_CACHE_KEY);
    if (raw) return { ...defaultAppearance, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return defaultAppearance;
}

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const passwordReveal = usePasswordReveal(password, setPassword);
  const confirmReveal = usePasswordReveal(confirmPassword, setConfirmPassword);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [appearance, setAppearance] = useState<AppearanceSettings>(loadCachedAppearance);

  useEffect(() => {
    publicApi.getAppearance()
      .then((data) => {
        setAppearance({ ...defaultAppearance, ...data });
        try { localStorage.setItem(APPEARANCE_CACHE_KEY, JSON.stringify(data)); } catch { /* ignore */ }
      })
      .catch(() => {});
  }, []);

  const primaryColor = appearance.primaryColor;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError(t('auth.password_too_short'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('auth.register_password_mismatch'));
      return;
    }

    if (!token) {
      setError(t('auth.reset_token_missing'));
      return;
    }

    setIsLoading(true);

    try {
      await authApi.resetPassword(token, password);
      setSuccess(true);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || t('auth.reset_failed'));
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-8">
        <div className="w-full max-w-md text-center">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mb-4">
            <AlertTriangle className="h-8 w-8 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.reset_link_invalid_title')}</h2>
          <p className="text-gray-500 mb-6">
            {t('auth.reset_link_invalid_msg')}
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-6 py-3 text-white font-medium rounded-xl transition-colors"
            style={{ backgroundColor: primaryColor }}
          >
            {t('auth.back_to_login')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-8">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl overflow-hidden shadow-lg">
              {appearance.logo ? (
                <img src={appearance.logo} alt={appearance.companyName} className="w-full h-full object-cover" />
              ) : (
                <img src="/logo.png" alt="TrackFleet" className="w-full h-full object-cover" />
              )}
            </div>
            <span className="text-2xl font-bold text-gray-900">{appearance.companyName}</span>
          </div>
        </div>

        {success ? (
          /* ---- Succès ---- */
          <div className="text-center">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-green-50 flex items-center justify-center mb-4">
              <CheckCircle2 className="h-8 w-8 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.reset_success_title')}</h2>
            <p className="text-gray-500 mb-6">
              {t('auth.reset_success_msg')}
            </p>
            <button
              onClick={() => navigate('/login')}
              className="inline-flex items-center gap-2 px-6 py-3 text-white font-medium rounded-xl transition-colors"
              style={{ backgroundColor: primaryColor }}
              onMouseOver={(e) => e.currentTarget.style.filter = 'brightness(0.9)'}
              onMouseOut={(e) => e.currentTarget.style.filter = ''}
            >
              {t('auth.sign_in')}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        ) : (
          /* ---- Formulaire ---- */
          <>
            <div className="text-center mb-8">
              <div className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: `${primaryColor}15` }}>
                <Lock className="h-8 w-8" style={{ color: primaryColor }} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.reset_form_title')}</h2>
              <p className="text-gray-500 text-sm">
                {t('auth.reset_form_subtitle')}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('auth.new_password')}
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      id="new-password"
                      type={passwordReveal.inputProps.type}
                      autoComplete="new-password"
                      required
                      minLength={8}
                      value={password}
                      onChange={passwordReveal.inputProps.onChange}
                      onKeyDown={passwordReveal.inputProps.onKeyDown}
                      onBeforeInput={passwordReveal.inputProps.onBeforeInput}
                      onPaste={passwordReveal.inputProps.onPaste}
                      className="w-full pl-12 pr-12 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 transition-colors"
                      onFocus={(e) => e.target.style.borderColor = primaryColor}
                      onBlur={(e) => e.target.style.borderColor = ''}
                      placeholder={t('auth.password_placeholder_min8')}
                    />
                    {passwordReveal.canReveal && (
                      <button
                        type="button"
                        onClick={passwordReveal.toggle}
                        aria-label={passwordReveal.visible ? t('auth.hide_password') : t('auth.show_password')}
                        aria-pressed={passwordReveal.visible}
                        title={passwordReveal.visible ? t('auth.hide_password') : t('auth.show_password')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none"
                      >
                        {passwordReveal.visible ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('auth.confirm_password_field')}
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      id="confirm-password"
                      type={confirmReveal.inputProps.type}
                      autoComplete="new-password"
                      required
                      minLength={8}
                      value={confirmPassword}
                      onChange={confirmReveal.inputProps.onChange}
                      onKeyDown={confirmReveal.inputProps.onKeyDown}
                      onBeforeInput={confirmReveal.inputProps.onBeforeInput}
                      onPaste={confirmReveal.inputProps.onPaste}
                      className="w-full pl-12 pr-12 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 transition-colors"
                      onFocus={(e) => e.target.style.borderColor = primaryColor}
                      onBlur={(e) => e.target.style.borderColor = ''}
                      placeholder={t('auth.confirm_password_placeholder')}
                    />
                    {confirmReveal.canReveal && (
                      <button
                        type="button"
                        onClick={confirmReveal.toggle}
                        aria-label={confirmReveal.visible ? t('auth.hide_password') : t('auth.show_password')}
                        aria-pressed={confirmReveal.visible}
                        title={confirmReveal.visible ? t('auth.hide_password') : t('auth.show_password')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none"
                      >
                        {confirmReveal.visible ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading || !password || !confirmPassword}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 text-white font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: primaryColor }}
                onMouseOver={(e) => !isLoading && (e.currentTarget.style.filter = 'brightness(0.9)')}
                onMouseOut={(e) => e.currentTarget.style.filter = ''}
              >
                {isLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    {t('auth.reset_button')}
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </form>

            <p className="text-center text-sm text-gray-500 mt-6">
              <Link
                to="/login"
                className="font-medium transition-colors"
                style={{ color: primaryColor }}
              >
                {t('auth.back_to_login')}
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
